// frontend/src/components/charts/SalesEvolutionChart.jsx
import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import './SalesEvolutionChart.css';

const SalesEvolutionChart = ({ data = [], period = '30j', onPeriodChange }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [chartData, setChartData] = useState([]);

  // Mettre à jour les données du graphique
  useEffect(() => {
    if (!data || data.length === 0) {
      // Données simulées pour l'affichage (à remplacer par tes vraies données)
      const simulatedData = generateSimulatedData(period);
      setChartData(simulatedData);
    } else {
      const formatted = data.map(item => ({
        date: new Date(item.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
        revenue: item.daily_revenue || 0,
        sales: item.daily_sales || 0
      }));
      setChartData(formatted);
    }
  }, [data, period]);

  // Générer des données simulées pour l'affichage
  const generateSimulatedData = (period) => {
    const days = period === '7j' ? 7 : period === '30j' ? 30 : 90;
    const result = [];
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      result.push({
        date: date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
        revenue: Math.floor(Math.random() * 500) + 100,
        sales: Math.floor(Math.random() * 20) + 1
      });
    }
    return result;
  };

  // Créer/Mettre à jour le graphique
  useEffect(() => {
    if (chartRef.current) {
      // Détruire l'ancien graphique s'il existe
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');
      
      chartInstanceRef.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: chartData.map(d => d.date),
          datasets: [
            {
              label: 'Chiffre d\'affaires (TND)',
              data: chartData.map(d => d.revenue),
              borderColor: '#3b82f6',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              borderWidth: 3,
              tension: 0.4,
              fill: true,
              pointBackgroundColor: '#3b82f6',
              pointBorderColor: 'white',
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBorderWidth: 2,
              yAxisID: 'y'
            },
            {
              label: 'Nombre de ventes',
              data: chartData.map(d => d.sales),
              borderColor: '#10b981',
              backgroundColor: 'rgba(16, 185, 129, 0.05)',
              borderWidth: 2,
              tension: 0.4,
              fill: false,
              pointBackgroundColor: '#10b981',
              pointBorderColor: 'white',
              pointRadius: 3,
              pointHoverRadius: 5,
              pointBorderWidth: 2,
              yAxisID: 'y1'
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              position: 'top',
              labels: {
                usePointStyle: true,
                boxWidth: 8,
                font: {
                  size: 12,
                  weight: '500'
                }
              }
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label || '';
                  if (label) {
                    label += ': ';
                  }
                  if (context.parsed.y !== null) {
                    if (context.dataset.label === 'Chiffre d\'affaires (TND)') {
                      label += context.parsed.y.toFixed(2) + ' TND';
                    } else {
                      label += context.parsed.y + ' ventes';
                    }
                  }
                  return label;
                }
              },
              backgroundColor: 'white',
              titleColor: '#0f172a',
              bodyColor: '#475569',
              borderColor: '#e2e8f0',
              borderWidth: 1,
              padding: 12,
              cornerRadius: 12,
              displayColors: true,
              boxPadding: 6
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Chiffre d\'affaires (TND)',
                color: '#3b82f6',
                font: {
                  weight: '500',
                  size: 11
                }
              },
              ticks: {
                callback: function(value) {
                  return value + ' TND';
                },
                stepSize: 200
              },
              grid: {
                color: '#f1f5f9',
                drawBorder: false
              }
            },
            y1: {
              beginAtZero: true,
              position: 'right',
              title: {
                display: true,
                text: 'Nombre de ventes',
                color: '#10b981',
                font: {
                  weight: '500',
                  size: 11
                }
              },
              ticks: {
                stepSize: 5
              },
              grid: {
                drawOnChartArea: false,
              }
            },
            x: {
              grid: {
                display: false,
                drawBorder: false
              },
              ticks: {
                maxRotation: 45,
                minRotation: 45,
                font: {
                  size: 10
                }
              }
            }
          }
        }
      });
    }

    // Nettoyage lors du démontage
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [chartData]);

  const handlePeriodChange = (newPeriod) => {
    if (onPeriodChange) {
      onPeriodChange(newPeriod);
    }
  };

  return (
    <div className="sales-chart-container">
      <div className="chart-header">
        <h3>
          <i className="fas fa-chart-line"></i>
          Évolution des ventes
        </h3>
        <div className="period-buttons">
          {['7j', '30j', '90j'].map(p => (
            <button
              key={p}
              className={`period-btn ${period === p ? 'active' : ''}`}
              onClick={() => handlePeriodChange(p)}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="chart-wrapper">
        <canvas ref={chartRef}></canvas>
      </div>

      {chartData.length === 0 && (
        <div className="chart-empty">
          <i className="fas fa-chart-line"></i>
          <p>Aucune donnée disponible pour cette période</p>
        </div>
      )}
    </div>
  );
};

export default SalesEvolutionChart;